import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { Poppins } from 'src/common';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ValidationErrorItem, IncidentCSV, ParseResult } from './types';
import { generateCsvTemplateLink, pathNames } from './util';
import { selectUserOption, pluralize } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import CSelect from 'src/components/form/Select';
import { IncidentType, incidentTypeOptions, QKeys } from 'src/api/types';
import { IncidentCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { IncidentInput, IncidentsQuery, useIncidentsQuery, useUpsertIncidentsMutation } from 'src/graphql/_generated';
import { convertDateToISO, convertToString, delimiters, validateRow } from 'src/utils/csv';
import { KeyType, useCache } from 'src/graphql/useCache';
import { incidentValidationSchema } from 'src/components/formComposed/IncidentForm/util';
import {
  CsvDropzoneWithTemplate,
  CsvValidationErrors,
  CsvUploadButtons,
  SCVSummary,
  DzoneWrap,
} from 'src/components/CsvUpload';

const pluralizeIncidents = (count: number) => pluralize({ count, singular: 'incident', plural: 'incidents' });

interface FromCsvProps {
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
  invalidateQueries: KeyType[];
}

export const FromCsv: React.FC<FromCsvProps> = ({ onClose, setIsScvDirty, state, invalidateQueries }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validIncidents, setValidIncidents] = useState<IncidentInput[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { file, setFile, setReadyForUpload } = state;

  const { data: { incidents: existingIncidents = [] } = {} } = useIncidentsQuery();

  const [upsertIncidents] = useUpsertIncidentsMutation();
  const { refetchQuery } = useCache();

  const parseCSVToJSON = useCallback(
    (file: File) => {
      const tryParse = (delimiter: string) =>
        new Promise<ParseResult<IncidentCSV>>((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            delimiter,
            complete: resolve,
            error: reject,
          });
        });

      (async () => {
        let result: ParseResult<IncidentCSV> | null = null;
        for (const delim of delimiters) {
          const parsed = await tryParse(delim);
          if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
            result = parsed;
            break;
          }
        }

        if (!result || !result.data.length) {
          setValidationErrors([
            { rowIndex: 0, errors: [{ path: 'Data', message: 'No incident data found in the file.' }] },
          ]);
          return;
        }

        if (result.data.length > 60) {
          setError('The number of incidents exceeds the limit of 60.');
          setFile(null);
          return;
        }

        const mappedIncidents: IncidentInput[] = result.data.map((el) => {
          return {
            ...(el[IncidentCsvHeaders.ID] && { id: convertToString(el[IncidentCsvHeaders.ID]) }),
            name: convertToString(el[IncidentCsvHeaders.Name] || ''),
            ...(el[IncidentCsvHeaders.RefID] && { ref: convertToString(el[IncidentCsvHeaders.RefID]) }),
            ...(el[IncidentCsvHeaders.Description] && {
              description: convertToString(el[IncidentCsvHeaders.Description]),
            }),
            ...(el[IncidentCsvHeaders.Source] && { source: convertToString(el[IncidentCsvHeaders.Source]) }),
            ...(el[IncidentCsvHeaders.Date] && {
              dateOfIncident: convertDateToISO(el[IncidentCsvHeaders.Date]),
            }),
            ...(el[IncidentCsvHeaders.Owner] && {
              owner: { text: convertToString(el[IncidentCsvHeaders.Owner]) },
            }),
            ...(el[IncidentCsvHeaders.Impact] && { impact: convertToString(el[IncidentCsvHeaders.Impact]) }),
            ...(el[IncidentCsvHeaders.FinancialImpactUpper] && {
              financialImpactUpper: el[IncidentCsvHeaders.FinancialImpactUpper],
            }),
            ...(el[IncidentCsvHeaders.FinancialImpactLower] && {
              financialImpactLower: el[IncidentCsvHeaders.FinancialImpactLower],
            }),
            ...(el[IncidentCsvHeaders.Type] && {
              incidentType: convertToString(el[IncidentCsvHeaders.Type]) as IncidentType,
            }),
            ...(el[IncidentCsvHeaders.Severity] && {
              severity: convertToString(el[IncidentCsvHeaders.Severity]),
            }),
          };
        });

        const promises = mappedIncidents.map((row, idx) =>
          validateRow({ row, rowIndex: idx + 1, schema: incidentValidationSchema, pathNames }),
        );
        const res = await Promise.all(promises);
        const errorLog = res.filter((el) => el?.errors?.length) as ValidationErrorItem[];

        if (errorLog.length) {
          setValidationErrors(errorLog);
          setIsUploadSuccessful(false);
          setReadyForUpload(false);
        } else {
          setValidIncidents(mappedIncidents);
          setValidationErrors([]);
          setError('');
          setReadyForUpload(true);
        }
      })();
    },
    [existingIncidents, setFile, setReadyForUpload],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Add Incident modal',
        tags: ['INCIDENT'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setError('');
        setIsUploadSuccessful(false);
        setValidationErrors([]);
        parseCSVToJSON(selectedFile);
        setIsScvDirty(true);
      }
    },
    [parseCSVToJSON, setFile, setIsScvDirty],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add Incident modal',
      tags: ['INCIDENT'],
    });

    setReadyForUpload(false);
    setIsUploading(true);

    const data = validIncidents.map((el) => {
      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: { text: el.owner?.text || '' },
      });
      return { ...el, owner: userAdd };
    });

    await upsertIncidents({ variables: { incidentsData: data, workspaceId } })
      .then(() => {
        queryCache.invalidateQueries([QKeys.Audit]);
        refetchQuery({ include: invalidateQueries });
        setIsScvDirty(false);
      })
      .catch((err: Error) => {
        setError(err?.message || 'Server error');
      });

    setIsUploading(false);
    setIsUploadSuccessful(true);
    setReadyForUpload(false);
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setReadyForUpload(false);
  };

  const newCount = validIncidents.filter((i) => !existingIncidents.some((e) => e.id === i.id)).length;
  const updatedCount = validIncidents.filter((i) => existingIncidents.some((e) => e.id === i.id)).length;

  return (
    <DzoneWrap className="h-padding">
      <CsvDropzoneWithTemplate
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        file={file}
        templateLink={generateCsvTemplateLink()}
        templateFileName="incident_template.csv"
        onTemplateClick={() =>
          mpEvent(MPEvents.DownloadCSVTemplate, {
            modal: 'Add Incident modal',
            tags: ['INCIDENT'],
          })
        }
      />

      <CsvValidationErrors errors={validationErrors} />

      {file && !validationErrors.length && (
        <>
          {!isUploadSuccessful && (
            <SCVSummary
              items={[
                'File validated',
                `${updatedCount} ${pluralizeIncidents(updatedCount)} ready to be updated`,
                `${newCount} ${pluralizeIncidents(newCount)} ready to be created`,
              ]}
            />
          )}
          {isUploadSuccessful && (
            <SCVSummary
              isCompleted
              items={[
                `${updatedCount} existing ${pluralizeIncidents(updatedCount)} updated`,
                `${newCount} new ${pluralizeIncidents(newCount)} added`,
                'File upload successful',
              ]}
            />
          )}
        </>
      )}

      <CsvUploadButtons
        isUploadSuccessful={isUploadSuccessful}
        hasFile={!!file}
        isDisabled={!file || !!validationErrors.length || isUploading}
        onCancel={onClose}
        onUpload={save}
        onSelectFile={open}
        onUploadAnother={resetUpload}
        onDone={onClose}
      />

      {!file && (
        <>
          <Poppins px={14} weight={500}>
            The valid values of Type are listed in the drop down below. Blank is also valid.
          </Poppins>
          <div className="grid-list grid-list--3">
            <CSelect value={null} placeholder="TYPE" menuPlacement="top" options={incidentTypeOptions} />
          </div>
        </>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
